import { ModalType } from "@/constants/modalConst"
import { FilteredSelection, QueryFullMarketsResponse } from "@/types/fullMarket"
import { ExchangeMultiMarket } from "@/types/multiMarket"
import { MaybeRefOrGetter } from "vue"
import {
  QueryGameHallResponseVendorType1,
  QueryVendorCurrencyInfoResponse
} from "@/types/common"

type ModalOptionsMap = {
  [ModalType.CONFIRM_BETS]: {
    confirm: () => void
    payload: {
      id: string
    }
  }
  [ModalType.MARKET_DEPTH]: {
    payload: {
      event: MaybeRefOrGetter<QueryFullMarketsResponse | ExchangeMultiMarket>
      selections: MaybeRefOrGetter<FilteredSelection[]>
      selectionId: MaybeRefOrGetter<string>
    }
  }
  [ModalType.BALANCE_TRANSFER]: {
    payload: {
      vendor: Partial<QueryGameHallResponseVendorType1>
      details: QueryVendorCurrencyInfoResponse
    }
  }
}

type ModalOptions<T extends ModalType> = T extends keyof ModalOptionsMap
  ? ModalOptionsMap[T]
  : undefined

export const useModalStore = <T extends ModalType = never>() => {
  const store = defineStore("modal", () => {
    const isVisible = ref(false)
    const catchModal = ref<ModalType | null>(null)
    const options = shallowRef<ModalOptions<T>>(null!)
    const currentModal = ref<ModalType | null>(null)

    const resetCatchModal = () => {
      catchModal.value = null
    }

    const resetModal = () => {
      currentModal.value = null
      options.value = null
    }

    const closeModal = () => {
      isVisible.value = false
    }

    const openModal = <R extends ModalType>(modalType: R, option?: ModalOptions<R>) => {
      if (currentModal.value === modalType) return
      if (option !== undefined) options.value = option
      isVisible.value = true

      if (currentModal.value !== null) {
        catchModal.value = modalType
        closeModal()
      } else {
        currentModal.value = modalType
      }
    }

    return {
      openModal,
      closeModal,
      resetModal,
      resetCatchModal,
      currentModal,
      catchModal,
      isVisible,
      options
    }
  })

  return store()
}

export default useModalStore
